import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
  CheckCircleIcon,
  UsersIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { groupsApi } from '@/api/groupsApi'
import toast from 'react-hot-toast'

interface Group {
  id: string
  name: string
  description: string
  memberCount: number
  isActive: boolean
  createdAt: string
}

// Form schema for adding new group
const addGroupSchema = z.object({
  name: z.string().min(2, 'Group name must be at least 2 characters'),
  description: z.string().min(5, 'Description must be at least 5 characters')
})

type AddGroupData = z.infer<typeof addGroupSchema>

const GroupsPage: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null)
  const [editingGroup, setEditingGroup] = useState<Group | null>(null)

  const addGroupForm = useForm<AddGroupData>({
    resolver: zodResolver(addGroupSchema),
    defaultValues: {
      name: '',
      description: ''
    }
  })

  const editGroupForm = useForm<AddGroupData>({
    resolver: zodResolver(addGroupSchema),
    defaultValues: {
      name: '',
      description: ''
    }
  })

  useEffect(() => {
    fetchGroups()
  }, [])

  const fetchGroups = async () => {
    setLoading(true)
    try {
      const apiGroups = await groupsApi.getGroups()
      // Convert API response to frontend format
      const formattedGroups: Group[] = apiGroups.map(group => ({
        id: group.id,
        name: group.name,
        description: group.description,
        memberCount: group.memberCount,
        isActive: group.isActive,
        createdAt: group.createdAt.split('T')[0] // Format date
      }))
      
      setGroups(formattedGroups)
    } catch (error) {
      toast.error('Failed to load groups')
    } finally {
      setLoading(false)
    }
  }

  const filteredGroups = groups.filter(group => {
    const matchesSearch = 
      group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      group.description.toLowerCase().includes(searchQuery.toLowerCase())
    
    return matchesSearch
  })

  const handleViewGroup = (group: Group) => {
    setSelectedGroup(group)
  }

  const handleEditGroup = (group: Group) => {
    setEditingGroup(group)
    editGroupForm.reset({
      name: group.name,
      description: group.description
    })
    setShowEditModal(true)
  }

  const handleToggleGroupStatus = async (group: Group) => {
    try {
      await groupsApi.updateGroup(group.id, { isActive: !group.isActive })
      const action = group.isActive ? 'deactivated' : 'activated'
      toast.success(`${group.name} ${action} successfully`)
      fetchGroups() // Refresh the list
    } catch (error) {
      toast.error('Failed to update group status')
    }
  }

  const handleUpdateGroup = async (groupData: AddGroupData) => {
    if (!editingGroup) return
    
    try {
      await groupsApi.updateGroup(editingGroup.id, groupData)
      toast.success(`${groupData.name} updated successfully!`)
      setShowEditModal(false)
      setEditingGroup(null)
      editGroupForm.reset()
      fetchGroups() // Refresh the list
      
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update group. Please try again.'
      toast.error(message)
    }
  }

  const handleDeleteGroup = async (group: Group) => {
    if (confirm(`Are you sure you want to delete ${group.name}?`)) {
      try {
        await groupsApi.deleteGroup(group.id)
        toast.success(`${group.name} deleted successfully`)
        fetchGroups() // Refresh the list
      } catch (error) {
        toast.error('Failed to delete group')
      }
    }
  }

  const handleAddGroup = async (groupData: AddGroupData) => {
    try {
      console.log('Creating group:', groupData)
      
      // Call real API
      await groupsApi.createGroup(groupData)
      
      toast.success(`${groupData.name} created successfully!`)
      setShowAddModal(false)
      addGroupForm.reset()
      fetchGroups() // Refresh the list
      
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to create group. Please try again.'
      toast.error(message)
    }
  }

  const GroupCard = ({ group }: { group: Group }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="dashboard-card hover:shadow-large transition-all duration-200"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-indigo-100 rounded-xl flex items-center justify-center">
            <UserGroupIcon className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
            <p className="text-sm text-gray-500">{group.memberCount} members</p>
          </div>
        </div>
        <span className={`badge ${group.isActive ? 'badge-success' : 'badge-gray'}`}>
          {group.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
        {group.description}
      </p>

      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Members</span>
          <span className="font-medium text-gray-900">{group.memberCount}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Created</span>
          <span className="font-medium text-gray-900">{group.createdAt}</span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewGroup(group)}
            className="btn btn-ghost text-xs"
          >
            <EyeIcon className="h-4 w-4 mr-1" />
            View
          </button>
          <button
            onClick={() => handleEditGroup(group)}
            className="btn btn-ghost text-xs"
          >
            <PencilIcon className="h-4 w-4 mr-1" />
            Edit
          </button>
          <button
            onClick={() => handleToggleGroupStatus(group)}
            className={`btn btn-ghost text-xs ${group.isActive ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`}
          >
            {group.isActive ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => handleDeleteGroup(group)}
            className="btn btn-ghost text-xs text-error-600 hover:bg-error-50"
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Delete
          </button>
        </div>
      </div>
    </motion.div>
  )

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="dashboard-card animate-pulse">
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Groups</h1>
          <p className="text-gray-600 mt-1">
            Organize users into groups for better management
          </p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Group
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Groups</p>
              <p className="text-2xl font-bold text-gray-900">{groups.length}</p>
            </div>
            <UserGroupIcon className="h-8 w-8 text-primary-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Groups</p>
              <p className="text-2xl font-bold text-gray-900">{groups.filter(g => g.isActive).length}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-success-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Members</p>
              <p className="text-2xl font-bold text-gray-900">{groups.reduce((sum, g) => sum + g.memberCount, 0)}</p>
            </div>
            <UsersIcon className="h-8 w-8 text-indigo-600" />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center justify-between">
        <div className="relative max-w-md">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-10"
          />
        </div>
        <span className="text-sm text-gray-600">
          {filteredGroups.length} groups
        </span>
      </div>

      {/* Groups Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {filteredGroups.map((group) => (
            <GroupCard key={group.id} group={group} />
          ))}
        </AnimatePresence>
      </div>

      {/* Empty State */}
      {filteredGroups.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="text-center py-12">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              {searchQuery ? 'No groups found' : 'No groups yet'}
            </h3>
            <p className="mt-2 text-gray-500">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Create your first group to organize users'
              }
            </p>
            {!searchQuery && (
              <button 
                onClick={() => setShowAddModal(true)}
                className="mt-4 btn btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create First Group
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Simple Add Group Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Create Group</h3>
                    <p className="text-sm text-gray-500">Create a new user group</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={addGroupForm.handleSubmit(handleAddGroup)} className="p-6 space-y-4">
                <Input
                  {...addGroupForm.register('name')}
                  label="Group Name"
                  placeholder="e.g.,Teacher"
                  error={addGroupForm.formState.errors.name?.message}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    {...addGroupForm.register('description')}
                    rows={3}
                    className="form-input"
                    placeholder="Describe the purpose of this group..."
                  />
                  {addGroupForm.formState.errors.description && (
                    <p className="mt-1 text-sm text-red-600">{addGroupForm.formState.errors.description.message}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-primary-600 hover:bg-primary-700"
                  >
                    Create Group
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Group Modal */}
      <AnimatePresence>
        {showEditModal && editingGroup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Edit Group</h3>
                    <p className="text-sm text-gray-500">Update group information</p>
                  </div>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={editGroupForm.handleSubmit(handleUpdateGroup)} className="p-6 space-y-4">
                <Input
                  {...editGroupForm.register('name')}
                  label="Group Name"
                  placeholder="e.g., Mathematics Department"
                  error={editGroupForm.formState.errors.name?.message}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    {...editGroupForm.register('description')}
                    rows={3}
                    className="form-input"
                    placeholder="Describe the purpose of this group..."
                  />
                  {editGroupForm.formState.errors.description && (
                    <p className="mt-1 text-sm text-red-600">{editGroupForm.formState.errors.description.message}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-primary-600 hover:bg-primary-700"
                  >
                    Update Group
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Simple Group Details Modal */}
      <AnimatePresence>
        {selectedGroup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedGroup(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedGroup.name}</h3>
                    <p className="text-sm text-gray-500">{selectedGroup.memberCount} members</p>
                  </div>
                  <button
                    onClick={() => setSelectedGroup(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                  <p className="text-gray-700">{selectedGroup.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Members:</span>
                    <span className="font-medium text-gray-900 ml-2">{selectedGroup.memberCount}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className={`ml-2 badge ${selectedGroup.isActive ? 'badge-success' : 'badge-gray'}`}>
                      {selectedGroup.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Created:</span>
                    <span className="font-medium text-gray-900 ml-2">{selectedGroup.createdAt}</span>
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t">
                  <Button
                    onClick={() => {
                      handleToggleGroupStatus(selectedGroup)
                      setSelectedGroup(null)
                    }}
                    variant={selectedGroup.isActive ? "secondary" : "primary"}
                    className={selectedGroup.isActive ? "text-orange-600 hover:bg-orange-50" : "text-green-600 hover:bg-green-50"}
                  >
                    {selectedGroup.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    onClick={() => {
                      handleEditGroup(selectedGroup)
                      setSelectedGroup(null)
                    }}
                    className="bg-primary-600 hover:bg-primary-700"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit Group
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default GroupsPage